import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { IGetEmailTwoFactorRes } from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import { useTwoFactorSettings } from "store/two-factor/two-factor-settings.store";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  status: IGetEmailTwoFactorRes | null;
  pendingEnable: boolean;
  recoveryCodes: string[] | null;
};

type Action = {
  fetchStatus: () => Promise<void>;
  beginEnable: (password: string | undefined, email: string) => Promise<void>;
  verifyEnable: (code: string) => Promise<void>;
  revoke: (password?: string) => Promise<void>;
  clearPendingEnable: () => void;
  clearRecoveryCodes: () => void;
};

export const useEmailTwoFactor = create(
  devtools<State & Action>(
    (set, get) => ({
      status: null,
      pendingEnable: false,
      recoveryCodes: null,

      fetchStatus: async () => {
        try {
          const status = await apiV2.emailTwoFactor.get();
          set({ status });
        } catch (error) {
          console.error("Failed to fetch email 2FA status:", error);
          set({ status: { configured: false, email: null, verifiedAt: null } });
        }
      },

      beginEnable: async (password, email) => {
        await apiV2.emailTwoFactor.enable({ password, email });
        set({ pendingEnable: true });
      },

      verifyEnable: async (code) => {
        const { recoveryCodes } = await apiV2.emailTwoFactor.verifyEnable({
          code,
        });
        set({
          pendingEnable: false,
          recoveryCodes: recoveryCodes?.length ? recoveryCodes : null,
        });
        useTwoFactorSettings.getState().setMethodConfigured("email", true);
        await get().fetchStatus();
      },

      revoke: async (password) => {
        await apiV2.emailTwoFactor.revoke({ password });
        useTwoFactorSettings.getState().setMethodConfigured("email", false);
        await get().fetchStatus();
      },

      clearPendingEnable: () => set({ pendingEnable: false }),
      clearRecoveryCodes: () => set({ recoveryCodes: null }),
    }),
    { name: "email-two-factor" }
  )
);

registerStore(useEmailTwoFactor);
